import React, { useState } from "react";
import { fetchBillingReconciliation } from "../../services/billingReconciliationService";
import { buildReconciliationCsv } from "./billingReconciliationCsv";
import { QuickBooksAdvertiserPicker } from "./QuickBooksAdvertiserPicker";
import { browserDownload, fieldInputStyle, fieldLabelStyle, rangeError } from "./quickbooksDownload";
import { useExportAdvertisers } from "./useExportAdvertisers";

// The QuickBooks page's Reconciliation tab, /support/quickbooks/reconciliation
// (DEV-2926): one CSV line per advertiser per UTC activity day, comparing what
// the UI shows as billable with what was charged, and why they differ. Rows come
// from migration 109. Stripe fees and net are not included: they reach the
// database only through the QuickBooks connector.

function summary(rows) {
  const advertisers = new Set(rows.map((row) => row.advertiser_id)).size;
  return `Downloaded ${rows.length.toLocaleString("en-US")} ${rows.length === 1 ? "row" : "rows"} for ${advertisers.toLocaleString("en-US")} ${advertisers === 1 ? "advertiser" : "advertisers"}.`;
}

export function QuickBooksReconciliationPage({ now = () => new Date(), download = browserDownload }) {
  const [start, setStart] = useState(() => `${now().toISOString().slice(0, 8)}01`);
  const [end, setEnd] = useState(() => now().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { tone: "status" | "alert", text }
  const { advertisers, advertisersError } = useExportAdvertisers();
  const [selected, setSelected] = useState(null); // null: every advertiser; else a Set of ids

  const invalid = rangeError(start, end);
  const noneSelected = selected !== null && selected.size === 0;
  // Wait for the list: every advertiser then goes by id, in small requests.
  const listLoading = advertisers === null && !advertisersError;
  const disabled = Boolean(invalid) || noneSelected || listLoading || busy;
  const includesToday = !invalid && end >= now().toISOString().slice(0, 10);

  const downloadCsv = async () => {
    setBusy(true);
    setResult(null);
    try {
      const ids = selected !== null ? [...selected] : advertisers?.map((a) => a.id) ?? null;
      const rows = await fetchBillingReconciliation(start, end, ids);
      if (rows.length === 0) {
        setResult({ tone: "status", text: "No billable or charged activity in this range." });
        return;
      }
      download(`billing-reconciliation-${start}-to-${end}.csv`, buildReconciliationCsv(rows));
      setResult({ tone: "status", text: summary(rows) });
    } catch (err) {
      setResult({ tone: "alert", text: err?.message || "Could not load the reconciliation." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#151515", margin: "0 0 6px" }}>Reconciliation</h2>
      <p style={{ color: "#626262", fontSize: 14, margin: "0 0 12px", maxWidth: "65ch" }}>
        One line per advertiser per day, on the UTC day the conversion happened, the day the dashboard shows it on. Billable is what the dashboard counts. Charged is what was charged or settled for that day's conversions, whenever it happened. Difference is billable minus charged, and Reason lists the conversions behind it, with what each group was charged, or its spend if not charged. Credits need a closer look: a conversion credited and never charged stays in Difference for good, and one charged then credited shows no difference, only a reason.
      </p>
      <p style={{ color: "#626262", fontSize: 14, margin: "0 0 24px", maxWidth: "65ch" }}>
        Charge Dated This Day is the total of the card payments in our records for that day. To compare it with Stripe, look one day later for an automatic charge: it runs the day after the day it covers. Payment settled offline is not in this column, and Stripe fees and net are not included.
      </p>

      <div style={{ background: "#FFFFFF", border: "1px solid #E2E2E2", borderRadius: 12, padding: 20 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 6 }}>
          <div>
            <label htmlFor="reconciliation-from" style={fieldLabelStyle}>From</label>
            <input id="reconciliation-from" type="date" value={start} onChange={(e) => setStart(e.target.value)} style={fieldInputStyle} />
          </div>
          <div>
            <label htmlFor="reconciliation-to" style={fieldLabelStyle}>To</label>
            <input id="reconciliation-to" type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={fieldInputStyle} />
          </div>
        </div>
        <p style={{ fontSize: 12, color: "#626262", margin: "0 0 18px" }}>Both days are included. Defaults to month to date. Running Difference starts at zero on the first day of the range. Recent days can show a difference until the next charge runs.</p>
        {invalid && <p style={{ fontSize: 12, color: "#B42318", margin: "-10px 0 18px" }}>{invalid}</p>}
        {includesToday && (
          <p style={{ fontSize: 12, color: "#8A5A00", margin: "-10px 0 18px" }}>
            Today (UTC) is not over, so its numbers can still change.
          </p>
        )}

        <QuickBooksAdvertiserPicker
          advertisers={advertisers}
          selected={selected}
          onChange={setSelected}
          error={advertisersError}
        />
        {noneSelected && <p style={{ fontSize: 12, color: "#B42318", margin: "-10px 0 18px" }}>Pick at least one advertiser.</p>}

        <button
          type="button"
          onClick={downloadCsv}
          disabled={disabled}
          style={{ padding: "10px 18px", background: disabled ? "#F0F0F0" : "#F90091", color: disabled ? "#B8B8B8" : "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer" }}
        >
          {busy ? "Downloading…" : "Download CSV"}
        </button>
        {result && (
          <p role={result.tone} style={{ fontSize: 13, color: result.tone === "alert" ? "#B42318" : "#3E3E3E", margin: "14px 0 0" }}>{result.text}</p>
        )}
      </div>
    </>
  );
}
